import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/Checkbox/Checkbox.module.scss. Hover, press and focus
// live on .interactive; checked, indeterminate and disabled live on .checkbox.

const parts: readonly AnatomyPart[] = [
    {
        id: "halo",
        label: "Hover halo",
        element:
            ".control::before, a 28x28 box behind the box, via --checkbox-halo",
    },
    {
        id: "border",
        label: "Box border",
        element: "1px border on the 16px .control, via --checkbox-border",
    },
    {
        id: "fill",
        label: "Box fill",
        element: "background on .control, via --checkbox-fill",
    },
    {
        id: "glyph",
        label: "Checkmark",
        element:
            ".glyph, holding the check or indeterminate vector, via --checkbox-glyph",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "outline on .control, from the shared focus-ring mixin",
    },
    {
        id: "label",
        label: "Label",
        element: "color and text style on .label",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector: ":not([data-disabled]):hover",
    },
    {
        id: "pressed",
        label: "Pressed",
        selector: ":not([data-disabled]):active",
    },
    {
        id: "focus",
        label: "Focus",
        selector: ":not([data-disabled]):focus-visible",
    },
    { id: "disabled", label: "Disabled", selector: "[data-disabled]" },
];

const fill = (token: `--${string}`): AnatomyBinding => ({
    partId: "fill",
    property: "background",
    token,
    via: "--checkbox-fill",
});

const edge = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--checkbox-border",
});

const halo = (token: `--${string}`): AnatomyBinding => ({
    partId: "halo",
    property: "background",
    token,
    via: "--checkbox-halo",
});

const glyph = (token: `--${string}`): AnatomyBinding => ({
    partId: "glyph",
    property: "color",
    token,
    via: "--checkbox-glyph",
});

const labelColor = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
});

const transparentHalo: AnatomyBinding = {
    partId: "halo",
    property: "background",
    literal: "transparent",
    via: "--checkbox-halo",
    note: "The halo box is always in the DOM; only its fill appears on hover, press and focus.",
};

const focusRingBindings: readonly AnatomyBinding[] = [
    {
        partId: "focusRing",
        property: "outline-width",
        literal: "2px",
        note: "focus-ring.ring-outline uses Sass $width, not --border-width-thick.",
    },
    {
        partId: "focusRing",
        property: "outline-color",
        token: "--focus-ring-color",
        note: "focus-ring.ring-outline uses Sass $blue-100, the same value this token carries.",
    },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "halo", property: "border-radius", token: "--radius-m" },
    {
        partId: "halo",
        property: "inset",
        literal: "-6px",
        note: "Figma geometry: a 28x28 halo and hit area overflowing the 16px layout box.",
    },
    { partId: "border", property: "width", literal: "16px" },
    { partId: "border", property: "border-width", literal: "1px" },
    { partId: "border", property: "border-radius", token: "--radius-xs" },
    {
        partId: "label",
        property: "column-gap",
        token: "--space-8",
        note: "Declared on the .checkbox row, spacing the control from its label.",
    },
    ...anatomyTextStyle({
        scale: "s",
        weight: "400",
        mixin: "text-s-14-regular",
    }),
];

const uncheckedRestingBindings: readonly AnatomyBinding[] = [
    fill("--fg-on-solid"),
    edge("--border-default"),
    transparentHalo,
    {
        partId: "glyph",
        property: "display",
        literal: "none",
        note: "Base UI renders the indicator only once the checkbox is checked or indeterminate.",
    },
    labelColor("--fg-primary"),
];

const tickedRestingBindings: readonly AnatomyBinding[] = [
    fill("--bg-accent"),
    edge("--bg-accent"),
    transparentHalo,
    { partId: "glyph", property: "display", literal: "block" },
    glyph("--fg-on-solid"),
    labelColor("--fg-primary"),
];

const tickedStateBindings: AnatomyVariant["stateBindings"] = {
    hover: [
        fill("--bg-accent-hover"),
        edge("--bg-accent-hover"),
        halo("--bg-neutral-subtle-hover"),
    ],
    pressed: [
        fill("--bg-accent-pressed"),
        edge("--bg-accent-pressed"),
        halo("--bg-neutral-subtle-hover"),
    ],
    focus: [
        {
            ...fill("--bg-accent"),
            note: "Focus keeps the resting fill. A checked radio on focus lifts to --bg-accent-hover instead — the two components diverge here.",
        },
        edge("--border-accent-focus"),
        halo("--bg-neutral-subtle-hover"),
        ...focusRingBindings,
    ],
    disabled: [
        fill("--bg-disabled"),
        edge("--bg-disabled"),
        glyph("--fg-tertiary"),
        labelColor("--fg-disabled"),
    ],
};

const variants: readonly AnatomyVariant[] = [
    {
        id: "unchecked",
        label: "Unchecked",
        selector: "",
        restingBindings: uncheckedRestingBindings,
        stateBindings: {
            hover: [edge("--border-hover"), halo("--bg-neutral-subtle-hover")],
            pressed: [
                fill("--bg-secondary"),
                edge("--border-hover"),
                halo("--bg-neutral-subtle-hover"),
            ],
            focus: [
                edge("--border-accent-focus"),
                halo("--bg-neutral-subtle-hover"),
                ...focusRingBindings,
            ],
            disabled: [edge("--border-disabled"), labelColor("--fg-disabled")],
        },
    },
    {
        id: "checked",
        label: "Checked",
        selector: "[data-checked]",
        restingBindings: tickedRestingBindings,
        stateBindings: tickedStateBindings,
    },
    {
        id: "indeterminate",
        label: "Indeterminate",
        selector: "[data-indeterminate]",
        restingBindings: [
            ...tickedRestingBindings,
            {
                partId: "glyph",
                property: "display",
                literal: "block",
                note: "Same token bindings as checked; only the vector swaps to the line glyph.",
            },
        ],
        stateBindings: tickedStateBindings,
    },
];

export const checkboxAnatomy: ComponentAnatomySpec = {
    label: "Checkbox",
    schematic: "checkbox",
    rootSelector: ".checkbox.interactive",
    variantAxisLabel: "Value",
    parts,
    states,
    sharedBindings,
    variants,
};
